import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { useAuth } from "./Contexts/AuthContext";
import { isAdminLike } from "../services/utils/utiltyFunctions";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import {
  BiWifi,
  BiWifi2,
  BiWifi1,
  BiWifiOff,
  BiSignal5,
  BiSignal3,
  BiSignal1,
  BiNetworkChart,
} from "react-icons/bi";
import { FaCog, FaTrash, FaRedo, FaUserCheck } from "react-icons/fa";
import {
  BsPlayFill,
  BsLightningChargeFill,
  BsClock,
  BsFileText,
  BsBatteryCharging,
  BsBatteryHalf,
} from "react-icons/bs";
import { ImSpinner2 } from "react-icons/im";
import "./ClaimedSystem.css";

const CHECK_INTERVAL_MS = 1 * 60 * 60 * 1000; // 1 hour

interface ClaimedSystem {
  id: number;
  system_name: string;
  patient_name: string;
  status: string;
  battery_lvl: number;
  charge_status: boolean;
  signalStrength: number;
  networkType: string;
  screenshot?: any;
  claimedby: string | null;
  note: string | null;
  last_checked: number | null;
  organizationId: string;
  isViewingOthers: boolean;
  isLiveView: boolean;
  lastSeenAt: string | number | null;
  ssProgress?: number;
  remoteSession?: string;
  moveSystemToDashboard: (id: number) => void;
  unclaimSystem: (id: number) => void;
  runCheck: (id: number) => Promise<void>;
  removeFromPool: (systemId: number, organizationId: string) => void;
  restartProcess: (systemId: number, organizationId: string) => void;
  onLiveView: (id: number) => void;
  onAssign?: (id: number) => void;
}

const ClaimedSystem: React.FC<ClaimedSystem> = ({
  id,
  system_name,
  patient_name,
  status,
  battery_lvl,
  charge_status,
  signalStrength,
  networkType,
  organizationId,
  isViewingOthers,
  isLiveView,
  lastSeenAt,
  ssProgress,
  remoteSession,
  unclaimSystem,
  runCheck,
  removeFromPool,
  restartProcess,
  onLiveView,
  onAssign,
  note,
  last_checked,
}) => {
  const { user } = useAuth();
  const isAdmin = isAdminLike(user?.role);
  const [gearOpen, setGearOpen] = useState(false);
  const [now, setNow] = useState(Date.now());
  const gearRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });

  useEffect(() => {
    if (!gearOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Element;
      // Don't close if click is on the gear button OR inside the portal'd menu
      if (gearRef.current?.contains(target)) return;
      if (target.closest?.(".sc-gear-menu")) return;
      setGearOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [gearOpen]);

  const openGear = () => {
    if (gearRef.current) {
      const rect = gearRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
    }
    setGearOpen((o) => !o);
  };

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  const isOffline = status === "Offline";
  const isInvalidHeadbox = status === "invalidHeadbox";
  const isNeuroworksClosed = status === "neuroworksClosed";
  // Source of truth for "system has a patient loaded" — works during the brief
  // initial-connection window where status hasn't transitioned to
  // neuroworksClosed yet but the system has reported no patient. Anything that
  // requires a study (run check, write note) should gate on this, not on the
  // less-reliable status string.
  const hasStudy = !!patient_name?.trim();
  const isBusy =
    status === "RUN_CHECK" ||
    status === "running_bluetooth" ||
    status === "taking_ss" ||
    status === "awaiting_approval" ||
    status === "writing_note";

  // ── Countdown logic ────────────────────────────────────────────
  const lastCheckedMs = last_checked
    ? typeof last_checked === "string"
      ? Number(last_checked)
      : last_checked
    : null;
  const nextCheckMs = lastCheckedMs ? lastCheckedMs + CHECK_INTERVAL_MS : null;
  const timeLeftMs = nextCheckMs ? nextCheckMs - now : null;
  const isOverdue = timeLeftMs !== null && timeLeftMs <= 0;
  const countdownPct =
    timeLeftMs !== null
      ? Math.max(0, Math.min(100, (timeLeftMs / CHECK_INTERVAL_MS) * 100))
      : 0;

  const getCountdownColor = () => {
    if (isOverdue) return "#dc2626";
    if (countdownPct > 50) return "#16a34a";
    if (countdownPct > 20) return "#d97706";
    return "#dc2626";
  };

  const formatTimeLeft = (ms: number) => {
    if (ms <= 0) return "Overdue";
    const totalMins = Math.floor(ms / 60_000);
    const hours = Math.floor(totalMins / 60);
    const mins = totalMins % 60;
    if (hours > 0) return `${hours}h ${mins}m left`;
    return `${mins}m left`;
  };

  const formatDueTime = (ts: number) =>
    new Date(ts).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });

  // ── Battery ────────────────────────────────────────────────────
  const getBatteryColor = (): string => {
    if (battery_lvl > 75) return "#16a34a";
    if (battery_lvl > 50) return "#d97706";
    if (battery_lvl > 25) return "#ea580c";
    return "#dc2626";
  };

  // ── Connection ─────────────────────────────────────────────────
  const getConnectionIcon = (size = 20) => {
    if (networkType === "cellular") {
      if (signalStrength < 150) return <BiSignal5 size={size} color="#16a34a" />;
      if (signalStrength < 300) return <BiSignal3 size={size} color="#d97706" />;
      return <BiSignal1 size={size} color="#dc2626" />;
    }
    if (networkType === "ethernet") {
      return (
        <BiNetworkChart
          size={size}
          color={signalStrength < 150 ? "#16a34a" : "#d97706"}
        />
      );
    }
    if (signalStrength < 150) return <BiWifi size={size} color="#16a34a" />;
    if (signalStrength < 300) return <BiWifi2 size={size} color="#d97706" />;
    if (signalStrength < 500) return <BiWifi1 size={size} color="#dc2626" />;
    return <BiWifiOff size={size} color="#dc2626" />;
  };

  const formatTimestamp = (timestamp: number | null) => {
    if (!timestamp) return "Never";
    const n = typeof timestamp === "string" ? Number(timestamp) : timestamp;
    if (isNaN(n)) return "Never";
    const d = new Date(n);
    if (isNaN(d.getTime())) return "Never";
    const m = d.getMonth() + 1;
    const day = d.getDate();
    let h = d.getHours();
    const min = String(d.getMinutes()).padStart(2, "0");
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    return `${m}/${day} ${h}:${min} ${ampm}`;
  };

  const getStatusBadge = () => {
    if (isOffline) return <span className="sc-badge sc-badge-offline">OFFLINE</span>;
    if (isInvalidHeadbox) return <span className="sc-badge sc-badge-warning">HEADBOX ERROR</span>;
    if (isNeuroworksClosed) return <span className="sc-badge sc-badge-orange">NO STUDY</span>;
    if (isBusy) return <span className="sc-badge sc-badge-blue">RUNNING</span>;
    if (isLiveView) return <span className="sc-badge sc-badge-live">LIVE</span>;
    return <span className="sc-badge sc-badge-online">ONLINE</span>;
  };

  const getRemoteToolBadge = () => {
    if (remoteSession === "teamviewer")
      return (
        <span className="sc-badge sc-badge-tool sc-tool-tv">
          <span className="sc-tool-full">TeamViewer</span>
          <span className="sc-tool-short">TV</span>
        </span>
      );
    if (remoteSession === "gotomypc")
      return (
        <span className="sc-badge sc-badge-tool sc-tool-g2">
          <span className="sc-tool-full">GoToMyPC</span>
          <span className="sc-tool-short">G2</span>
        </span>
      );
    return null;
  };

  const getStatusIcon = () => {
    if (isOffline) return <BiWifiOff size={20} color="#dc2626" />;
    if (isInvalidHeadbox) return <BiWifiOff size={20} color="#d97706" />;
    if (isNeuroworksClosed) return <BiWifiOff size={20} color="#ea580c" />;
    return (
      <OverlayTrigger
        placement="bottom"
        overlay={
          <Tooltip id={`tip-conn-${id}`}>
            {signalStrength > 0 ? `${signalStrength}ms` : "No ping data"}
          </Tooltip>
        }
      >
        <span style={{ cursor: "default", display: "flex" }}>
          {getConnectionIcon(20)}
        </span>
      </OverlayTrigger>
    );
  };

  const hasRemoteSession =
    remoteSession === "teamviewer" || remoteSession === "gotomypc";

  const getRunCheckLabel = () => {
    if (status === "RUN_CHECK") return <><ImSpinner2 className="sc-spinner" /> Running...</>;
    if (status === "running_bluetooth") return <><ImSpinner2 className="sc-spinner" /> Bluetooth...</>;
    if (status === "taking_ss") {
      const pctLabel = typeof ssProgress === "number" ? ` ${ssProgress}%` : "...";
      return <><ImSpinner2 className="sc-spinner" /> Screenshot{pctLabel}</>;
    }
    if (status === "awaiting_approval") return "Awaiting Approval";
    if (status === "writing_note") return <><ImSpinner2 className="sc-spinner" /> Writing Note...</>;
    if (hasRemoteSession) return "Remote Session Active";
    return <><BsPlayFill size={12} style={{ marginRight: 5 }} />Run Check</>;
  };

  const runCheckDisabled =
    isViewingOthers || isInvalidHeadbox || !hasStudy || isOffline || isBusy || hasRemoteSession;
  // Live view is allowed even with no study — operators can connect to
  // investigate why NeuroWorks isn't open, help start a study, etc.
  const connectDisabled = isViewingOthers || isOffline;

  const getAlertBox = () => {
    if (isOffline) {
      return (
        <div className="sc-alert sc-alert-offline">
          <div className="sc-alert-icon"><BiWifiOff size={18} color="#dc2626" /></div>
          <div><div className="sc-alert-title">System is offline</div></div>
        </div>
      );
    }
    if (isInvalidHeadbox) {
      return (
        <div className="sc-alert sc-alert-warning">
          <div className="sc-alert-icon"><BiWifiOff size={18} color="#d97706" /></div>
          <div>
            <div className="sc-alert-title">Invalid headbox connected</div>
          </div>
        </div>
      );
    }
    if (isNeuroworksClosed) {
      return (
        <div className="sc-alert sc-alert-orange">
          <div className="sc-alert-icon"><BiWifiOff size={18} color="#ea580c" /></div>
          <div>
            <div className="sc-alert-title">No active study</div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Countdown visible whenever we have a real last-checked timestamp, even
  // for offline systems. last_checked persists in Redis through deploys
  // and through brief disconnects, so the timer is still meaningful — the
  // operator wants to know "this system needs a check in 5 minutes" even
  // if it's currently unreachable, so they can prioritize when it comes
  // back. Offline countdowns get a muted style below to indicate the
  // status info is stale, not the timer itself. Still hidden for
  // invalidHeadbox / neuroworksClosed because those states genuinely mean
  // a check can't happen.
  const showCountdown =
    !isInvalidHeadbox && !isNeuroworksClosed && lastCheckedMs !== null;

  return (
    <div className={`sc${isOffline ? " sc-tint-offline" : isInvalidHeadbox ? " sc-tint-warning" : isNeuroworksClosed ? " sc-tint-orange" : isLiveView ? " sc-tint-live" : ""}`}>
      {/* Header */}
      <div className="sc-header">
        <div className="sc-header-left">
          {getStatusIcon()}
          {getStatusBadge()}
          {getRemoteToolBadge()}
          <OverlayTrigger
            placement="bottom"
            overlay={
              <Tooltip id={`tip-bat-${id}`}>
                {battery_lvl}%{charge_status ? " · Charging" : ""}
              </Tooltip>
            }
          >
            <span className="sc-battery-pill" style={{ color: getBatteryColor() }}>
              {charge_status ? (
                <BsBatteryCharging size={14} />
              ) : (
                <BsBatteryHalf size={14} />
              )}
              <span>{battery_lvl}%</span>
            </span>
          </OverlayTrigger>
        </div>
        <div className="sc-header-right">
          <button className="sc-unclaim-btn" onClick={() => unclaimSystem(id)}>
            Unclaim
          </button>

          {isAdmin && (
            <div ref={gearRef}>
              <button className="sc-gear-btn" onClick={openGear}>
                <FaCog size={13} />
              </button>
              {gearOpen && ReactDOM.createPortal(
                <div
                  className="sc-gear-menu"
                  style={{ position: "fixed", top: menuPos.top, right: menuPos.right, left: "auto" }}
                >
                  <button
                    className="sc-gear-menu-item sc-gear-menu-item-neutral"
                    onClick={() => {
                      setGearOpen(false);
                      restartProcess(id, organizationId);
                    }}
                  >
                    <FaRedo size={11} />
                    Restart process
                  </button>
                  {onAssign && (
                    <button
                      className="sc-gear-menu-item sc-gear-menu-item-neutral"
                      onClick={() => {
                        setGearOpen(false);
                        onAssign(id);
                      }}
                    >
                      <FaUserCheck size={12} />
                      Assign to user
                    </button>
                  )}
                  <button
                    className="sc-gear-menu-item"
                    onClick={() => {
                      setGearOpen(false);
                      removeFromPool(id, organizationId);
                    }}
                  >
                    <FaTrash size={12} />
                    Remove from pool
                  </button>
                </div>,
                document.body
              )}
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="sc-body">
        <div className="sc-patient-name">{patient_name}</div>
        <div className="sc-system-name">{system_name}</div>

        {getAlertBox()}

        {/* Countdown to next check. When the system is offline we still
            render this (last_checked persists in Redis) but desaturate
            everything so the visual signal is clearly "last known timer,
            system isn't currently reporting in." */}
        {showCountdown && (
          <div
            className="sc-countdown"
            style={isOffline ? { opacity: 0.55, filter: "grayscale(0.6)" } : undefined}
          >
            <div className="sc-countdown-row">
              <span className="sc-countdown-label">
                {isOffline ? "Last known countdown" : "Next check in"}
              </span>
              <span
                className="sc-countdown-value"
                style={{ color: isOffline ? "#6b7280" : getCountdownColor() }}
              >
                {formatTimeLeft(timeLeftMs!)}
              </span>
            </div>
            <div className="sc-countdown-track">
              <div
                className="sc-countdown-fill"
                style={{
                  width: `${countdownPct}%`,
                  backgroundColor: isOffline ? "#9ca3af" : getCountdownColor(),
                }}
              />
            </div>
            <div className="sc-countdown-due">
              Due at {formatDueTime(nextCheckMs!)}
            </div>
          </div>
        )}

        {/* Last seen row for offline */}
        {isOffline && (
          <div className="sc-last-seen">
            <span className="sc-last-seen-label">Last seen</span>
            <span className="sc-last-seen-val">
              {formatTimestamp(
                typeof lastSeenAt === "string" ? Number(lastSeenAt) : lastSeenAt
              )}
            </span>
          </div>
        )}

        {/* Buttons */}
        {!isOffline && (
          <div className="sc-btn-row">
            <button
              className="sc-btn-run"
              onClick={() => runCheck(id)}
              disabled={runCheckDisabled}
            >
              {getRunCheckLabel()}
            </button>
            <button
              className="sc-btn-connect"
              onClick={() => onLiveView(id)}
              disabled={connectDisabled}
            >
              <BsLightningChargeFill size={12} style={{ marginRight: 5 }} />
              Connect
            </button>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="sc-footer">
        <div className="sc-footer-item">
          <BsClock size={12} style={{ marginRight: 5, flexShrink: 0 }} />
          <span>Last checked {formatTimestamp(last_checked)}</span>
        </div>
        <div className="sc-footer-item">
          <BsFileText size={12} style={{ marginRight: 5, flexShrink: 0 }} />
          <span>{note || "No notes"}</span>
        </div>
      </div>
    </div>
  );
};

export default ClaimedSystem;
